import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';
import { Button, Textarea, InputLabel } from '../ui';
import QuestionTypeSelector from './QuestionTypeSelector';
import OptionsBuilder from './OptionsBuilder';
import StimulusPicker from './StimulusPicker';

const DEFAULT_OPTIONS = [
  { text: '', correct: true },
  { text: '', correct: false },
  { text: '', correct: false },
  { text: '', correct: false },
];

const BLOOM_LEVELS = ['remember', 'understand', 'apply', 'analyze', 'evaluate', 'create'];

export default function QuestionCreationForm({ lessonId, activityId, stimuli = [], onCreated, onCancel }) {
  const { t } = useTranslation();
  const [questionType, setQuestionType] = useState('short_answer');
  const [prompt, setPrompt] = useState('');
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [stimulusId, setStimulusId] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [bloomLevel, setBloomLevel] = useState('');
  const [explanation, setExplanation] = useState('');
  const [modelAnswer, setModelAnswer] = useState('');
  const [rubric, setRubric] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);

  const isMcq = questionType === 'mcq' || questionType === 'multiple_choice';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors([]);

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    const body = {
      question: {
        question_type: questionType,
        prompt,
        correct_answer: isMcq
          ? options.find((o) => o.correct)?.text || ''
          : correctAnswer,
        options: isMcq ? options.reduce((acc, opt, i) => ({ ...acc, [i]: opt }), {}) : null,
        stimulus_id: stimulusId,
        bloom_level: bloomLevel || null,
        explanation: explanation || null,
        model_answer: modelAnswer || null,
        rubric: rubric || null,
        position: 0,
      },
    };

    try {
      const createUrl = activityId
        ? `/teacher/activities/${activityId}/questions`
        : `/teacher/lessons/${lessonId}/questions`;
      const res = await fetch(createUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        const data = await res.json();
        onCreated?.(data.question);
      } else {
        const data = await res.json();
        setErrors(data.errors || ['Failed to create question']);
      }
    } catch {
      setErrors(['Network error']);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Question Type */}
      <div>
        <InputLabel className="mb-2">{t('questions.questionType')}</InputLabel>
        <QuestionTypeSelector
          value={questionType}
          onChange={(type) => {
            if (isMcq && type !== 'mcq' && type !== 'multiple_choice') {
              if (options.some((o) => o.text) && !confirm(t('questions.switchTypeWarning'))) return;
            }
            setQuestionType(type);
          }}
        />
      </div>

      {/* Prompt */}
      <div>
        <InputLabel htmlFor="question-prompt" required>{t('questions.prompt')}</InputLabel>
        <Textarea
          id="question-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={t('questions.promptPlaceholder')}
          rows={3}
          autoExpand
        />
      </div>

      {/* MCQ Options */}
      {isMcq && (
        <div>
          <InputLabel className="mb-2">{t('questions.options')}</InputLabel>
          <OptionsBuilder options={options} onChange={setOptions} />
        </div>
      )}

      {/* Correct Answer (non-MCQ) */}
      {!isMcq && (
        <div>
          <InputLabel htmlFor="correct-answer">{t('questions.correctAnswer')}</InputLabel>
          <Textarea
            id="correct-answer"
            value={correctAnswer}
            onChange={(e) => setCorrectAnswer(e.target.value)}
            placeholder={t('questions.expectedAnswer')}
            rows={2}
          />
        </div>
      )}

      {/* Stimulus */}
      {stimuli.length > 0 && (
        <div>
          <InputLabel>{t('questions.linkToStimulus')}</InputLabel>
          <StimulusPicker stimuli={stimuli} value={stimulusId} onChange={setStimulusId} />
        </div>
      )}

      {/* Advanced */}
      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex items-center gap-1"
        >
          <svg
            className={cn('w-3.5 h-3.5 transition-transform duration-200', showAdvanced && 'rotate-90')}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {t('questions.advanced')}
        </button>

        {showAdvanced && (
          <div className="space-y-4 mt-3 pl-5 border-l-2 border-gray-100 dark:border-gray-700">
            <div>
              <InputLabel>{t('questions.bloomsLevel')}</InputLabel>
              <select
                value={bloomLevel}
                onChange={(e) => setBloomLevel(e.target.value)}
                className={cn(
                  'w-full px-3 py-2 rounded-lg text-sm',
                  'bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-600',
                  'text-gray-900 dark:text-white',
                  'focus:outline-none focus:border-gray-400 dark:focus:border-gray-500'
                )}
              >
                <option value="">{t('common.notSet')}</option>
                {BLOOM_LEVELS.map((level) => (
                  <option key={level} value={level} className="capitalize">
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <InputLabel htmlFor="explanation">{t('questions.explanation')}</InputLabel>
              <Textarea
                id="explanation"
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                placeholder={t('questions.explanationPlaceholder')}
                rows={2}
              />
            </div>

            <div>
              <InputLabel htmlFor="model-answer">{t('questions.modelAnswer')}</InputLabel>
              <Textarea
                id="model-answer"
                value={modelAnswer}
                onChange={(e) => setModelAnswer(e.target.value)}
                placeholder={t('questions.modelAnswerPlaceholder')}
                rows={2}
              />
            </div>

            <div>
              <InputLabel htmlFor="rubric">{t('questions.rubric')}</InputLabel>
              <Textarea
                id="rubric"
                value={rubric}
                onChange={(e) => setRubric(e.target.value)}
                placeholder={t('questions.rubricPlaceholder')}
                rows={2}
              />
            </div>
          </div>
        )}
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="text-sm text-red-500">
          {errors.map((err, i) => <p key={i}>{err}</p>)}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <Button variant="ghost" size="sm" onClick={onCancel} type="button">
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          type="submit"
          disabled={!prompt.trim() || submitting}
        >
          {submitting ? t('common.processing') : t('questions.createQuestion')}
        </Button>
      </div>
    </form>
  );
}
